import Checkbox from 'components/Form/Checkbox';
import Field from 'components/Form/Field';
import FormResult from 'components/Form/FormResult';
import InputEmail from 'components/Form/InputEmail';
import SubmitButton from 'components/Form/SubmitButton';
import Icon from 'components/Icon';
import Layout from 'components/Layout';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {jt, t} from 'ttag';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';

import {useSendMagicLink} from './services';

type FormData = FormFields<{
    email: string;
    remember: boolean;
}>;

type MagicLinkFormProps = {
    defaultEmail: string;
    hasRemember: boolean;
    next: string;
    partnerName?: string;
    sessionMinutes?: number;
};

const MagicLinkForm: FC<MagicLinkFormProps> = ({
    defaultEmail,
    hasRemember,
    next,
    partnerName,
    sessionMinutes,
}) => {
    const methods = useForm<FormData>({
        defaultValues: {
            email: defaultEmail,
            remember: hasRemember,
        },
    });
    const sendLink = useSendMagicLink();
    const {handleSubmit} = methods;

    const onSendLink = async ({email, remember}: FormData) => {
        const submitData = {
            email,
            next,
            remember,
            session_minutes: sessionMinutes,
        };

        await sendLink
            .mutateAsync(submitData)
            .then(() => {
                setUrl(
                    `${window.Urls.portal_send_link_success_view()}?email=${encodeURIComponent(
                        email,
                    )}`,
                );
            })
            .catch(onApiFormError(methods));
    };

    const partnerSubtitle = (() => {
        const strong = <strong>{partnerName}</strong>;
        return jt`${strong} has Documents for you in their Client Inbox. Request a login link below.`;
    })();

    return (
        <div className="rounded-lg border border-grey-300 bg-white px-6 pb-12 pt-6 shadow-lg">
            <FormProvider {...methods}>
                <h1 className="mt-2">{t`Welcome to Business Portals by MakeLeaps`}</h1>
                {partnerName ? (
                    <p className="my-2 text-base">{partnerSubtitle}</p>
                ) : (
                    <p className="my-2 text-base">{t`To access your Business Portals, please input your business email address here to receive a Login Link:`}</p>
                )}
                <form onSubmit={handleSubmit(onSendLink)}>
                    <Layout.Stacked className="hide-required">
                        <div className="mt-4">
                            <Field
                                className="flex flex-col sm:flex-row"
                                name="email"
                                type="input"
                            >
                                <InputEmail
                                    // eslint-disable-next-line jsx-a11y/no-autofocus
                                    autoFocus={true}
                                    className="grow"
                                    label={t`Email Address`}
                                    name="email"
                                    required={true}
                                    requiredType="asterisk"
                                />
                                <SubmitButton
                                    aria-label="send-magic-link"
                                    className="mb-auto mt-6 align-top sm:ml-3"
                                    kind="primary"
                                >
                                    {t`Send Login Link`}
                                </SubmitButton>
                            </Field>
                            <Checkbox
                                className="mt-4"
                                label={
                                    <div className="text-sm">
                                        {t`Remember Me`}
                                    </div>
                                }
                                name="remember"
                            />
                        </div>
                    </Layout.Stacked>
                </form>
                <FormResult className="mt-4" />
            </FormProvider>
            <div className="mt-8 flex flex-row">
                <a
                    className="flex grow"
                    href="https://help.makeleaps.jp/ja/articles/8687205"
                    rel="noreferrer"
                    target="_blank"
                >
                    <Icon
                        className="my-1 mr-2 text-green-700"
                        icon="open_blank"
                        size="base"
                    />
                    {t`Business Portals User Manual`}
                </a>
                {sessionMinutes && (
                    <span className="text-sm text-grey-500">
                        {t`${sessionMinutes} min.`}
                    </span>
                )}
            </div>
        </div>
    );
};

export default MagicLinkForm;
